import React, { useEffect, useState, useCallback } from 'react';
import { FileBox, Download, Check, X, Pencil, Eye, EyeOff, ChevronDown, ChevronUp, Plus, Minus } from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';

const PRACTICE_AREAS = {
  personal_injury: { label: 'Personal Injury', color: 'bg-blue-100 text-blue-700', border: 'border-blue-200' },
  family_law: { label: 'Family Law', color: 'bg-purple-100 text-purple-700', border: 'border-purple-200' },
  general: { label: 'General', color: 'bg-gray-100 text-gray-700', border: 'border-gray-200' },
};

const PA_OPTIONS = [
  { value: 'personal_injury', label: 'Personal Injury' },
  { value: 'family_law', label: 'Family Law' },
  { value: 'general', label: 'General' },
];

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editModal, setEditModal] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/platform/workflow-templates');
      setTemplates(res.data.templates || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const toggleActive = async (template) => {
    const newActive = !template.is_active;
    try {
      const res = await api.patch(`/platform/workflow-templates/${template.id}`, { is_active: newActive });
      const updated = res.data.template;
      setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, ...updated } : t));
      toast.success(newActive ? 'Template published' : 'Template unpublished');
    } catch {
      toast.error('Failed to update');
    }
    setConfirmAction(null);
  };

  const saveEdit = async (id, data) => {
    try {
      const res = await api.patch(`/platform/workflow-templates/${id}`, data);
      const updated = res.data.template;
      setTemplates(prev => prev.map(t => t.id === id ? { ...t, ...updated } : t));
      toast.success(`Saved — now v${updated.version}`);
      setEditModal(null);
    } catch {
      toast.error('Failed to save');
    }
  };

  const loadPreview = async (id) => {
    if (previewId === id) { setPreviewId(null); return; }
    setPreviewId(id);
    setPreviewLoading(true);
    try {
      const res = await api.get(`/platform/workflow-templates/${id}`);
      setPreviewData(res.data.template?.template_data || {});
    } catch {
      toast.error('Failed to load preview');
      setPreviewId(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const filtered = filter === 'all' ? templates : templates.filter(t => t.practice_area === filter);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[1,2,3,4,5].map(i => <div key={i} className="h-56 bg-gray-200 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileBox className="w-6 h-6 text-gray-400" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Workflow Templates</h1>
            <p className="text-sm text-gray-500">Manage your template shop</p>
          </div>
        </div>
        <span className="text-sm text-gray-400">{templates.length} templates</span>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-6">
        {[{ key: 'all', label: 'All' }, ...PA_OPTIONS.map(o => ({ key: o.value, label: o.label }))].map(f => {
          const count = f.key === 'all' ? templates.length : templates.filter(t => t.practice_area === f.key).length;
          if (count === 0 && f.key !== 'all') return null;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                filter === f.key ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {f.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Template cards — 3 column grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map(template => (
          <TemplateCard
            key={template.id}
            template={template}
            onEdit={() => setEditModal(template)}
            onToggle={() => {
              if (template.is_active) {
                setConfirmAction(template);
              } else {
                toggleActive(template);
              }
            }}
            onPreview={() => loadPreview(template.id)}
            isPreviewOpen={previewId === template.id}
            previewLoading={previewLoading && previewId === template.id}
            previewData={previewId === template.id ? previewData : null}
          />
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center text-gray-400 py-12">No templates match this filter</div>
      )}

      {/* Unpublish confirmation */}
      {confirmAction && (
        <ConfirmModal
          template={confirmAction}
          onConfirm={() => toggleActive(confirmAction)}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {/* Edit modal */}
      {editModal && (
        <EditModal
          template={editModal}
          onSave={(data) => saveEdit(editModal.id, data)}
          onClose={() => setEditModal(null)}
        />
      )}
    </div>
  );
}

// ─── Template Card ───
function TemplateCard({ template, onEdit, onToggle, onPreview, isPreviewOpen, previewLoading, previewData }) {
  const pa = PRACTICE_AREAS[template.practice_area] || PRACTICE_AREAS.general;
  const features = template.features || [];
  const [showAll, setShowAll] = useState(false);
  const iconColor = template.color || '#6366F1';

  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-opacity ${
      !template.is_active ? 'opacity-60' : ''
    }`}>
      <div className="p-5">
        {/* Header with icon */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: iconColor + '18', color: iconColor }}>
            <FileBox className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="text-base font-semibold text-gray-900 truncate">{template.name}</h3>
              {template.is_lite && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 font-medium">Lite</span>}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${pa.color}`}>{pa.label}</span>
              <span className="text-[11px] text-gray-400 font-mono">{template.slug}</span>
              {template.is_lite && template.full_version_slug && (
                <span className="text-[10px] text-gray-400">Full: {template.full_version_slug}</span>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {template.description && (
          <p className="text-sm text-gray-500 mb-3 line-clamp-2">{template.description}</p>
        )}

        {/* Features */}
        {features.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {features.slice(0, showAll ? features.length : 3).map((f, i) => (
              <span key={i} className="text-[11px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-600 border border-gray-100">{f}</span>
            ))}
            {features.length > 3 && (
              <button onClick={() => setShowAll(!showAll)}
                className="text-[11px] px-1.5 py-0.5 rounded bg-gray-50 text-brand-600 border border-gray-100 hover:bg-brand-50">
                {showAll ? 'less' : `+${features.length - 3}`}
              </button>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-3 text-xs text-gray-500 pt-3 border-t border-gray-100">
          <span className="font-medium text-gray-700">v{template.version}</span>
          <div className="flex items-center gap-1">
            <Download className="w-3 h-3" />
            {template.install_count || 0} firms
          </div>
          {template.price_type === 'premium' ? (
            <span className="px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700">
              ${((template.price_cents || 0) / 100).toFixed(0)}{template.price_interval === 'annual' ? '/yr' : ''}
            </span>
          ) : template.price_type === 'included' ? (
            <span className="px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-600">Included</span>
          ) : (
            <span className="px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-green-50 text-green-600">Free</span>
          )}
          <div className="flex items-center gap-1">
            {template.is_active
              ? <><span className="w-1.5 h-1.5 rounded-full bg-green-500" /><span className="text-green-600">Published</span></>
              : <><span className="w-1.5 h-1.5 rounded-full bg-gray-400" /><span>Unpublished</span></>
            }
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center gap-2">
        <button onClick={onEdit}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
          <Pencil className="w-3 h-3" /> Edit
        </button>
        <button onClick={onPreview}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
          {isPreviewOpen ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          {isPreviewOpen ? 'Hide' : 'Preview'}
        </button>
        <button onClick={onToggle}
          className={`ml-auto px-3 py-1.5 text-xs font-medium rounded-lg ${
            template.is_active
              ? 'text-red-600 bg-red-50 border border-red-200 hover:bg-red-100'
              : 'text-green-700 bg-green-50 border border-green-200 hover:bg-green-100'
          }`}>
          {template.is_active ? 'Unpublish' : 'Publish'}
        </button>
      </div>

      {/* Preview panel */}
      {isPreviewOpen && (
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          {previewLoading ? (
            <div className="text-center text-sm text-gray-400 py-4">Loading...</div>
          ) : previewData?.workflow ? (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-white rounded-lg border border-gray-200 p-2">
                  <div className="text-lg font-bold text-gray-900">{previewData.workflow.stages?.length || 0}</div>
                  <div className="text-[10px] text-gray-500">Stages</div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-2">
                  <div className="text-lg font-bold text-gray-900">{previewData.workflow.transitions?.length || 0}</div>
                  <div className="text-[10px] text-gray-500">Transitions</div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-2">
                  <div className="text-lg font-bold text-gray-900">
                    {(previewData.workflow.stages || []).reduce((s, st) => s + (st.tasks?.length || 0), 0)}
                  </div>
                  <div className="text-[10px] text-gray-500">Tasks</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {(previewData.workflow.stages || []).map((s, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-white border border-gray-200 text-gray-700">
                    {s.name} <span className="text-gray-400">({s.tasks?.length || 0})</span>
                  </span>
                ))}
              </div>
              {previewData.fields && (
                <div className="text-xs text-gray-400">{previewData.fields.length} fields, {previewData.screens?.length || 0} screens</div>
              )}
            </div>
          ) : (
            <div className="text-center text-sm text-gray-400 py-4">No template data</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Unpublish Confirmation Modal ───
function ConfirmModal({ template, onConfirm, onCancel }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Unpublish "{template.name}"?</h3>
        <p className="text-sm text-gray-500 mb-2">
          Unpublishing {template.name} will hide it from the template shop.
          Tenants who already installed it keep their workflows and data unaffected.
        </p>
        <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-4">
          This does not delete any installed workflows or data. Continue?
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }} disabled={busy}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
            {busy ? 'Unpublishing...' : 'Unpublish'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Modal ───
function EditModal({ template, onSave, onClose }) {
  const [form, setForm] = useState({
    name: template.name || '',
    description: template.description || '',
    practice_area: template.practice_area || 'general',
    icon: template.icon || 'workflow',
    color: template.color || '#6366F1',
    features: [...(template.features || [])],
    tags: [...(template.tags || [])],
    price_type: template.price_type || 'free',
    price_cents: template.price_cents || 0,
    price_description: template.price_description || 'Free',
    price_interval: template.price_interval || null,
    is_lite: template.is_lite || false,
    full_version_slug: template.full_version_slug || '',
  });
  const [newFeature, setNewFeature] = useState('');
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addFeature = () => {
    const val = newFeature.trim();
    if (val && !form.features.includes(val)) {
      set('features', [...form.features, val]);
      setNewFeature('');
    }
  };

  const addTag = () => {
    const val = newTag.trim().toLowerCase();
    if (val && !form.tags.includes(val)) {
      set('tags', [...form.tags, val]);
      setNewTag('');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Edit Template</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
          </div>

          {/* Practice Area */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Practice Area</label>
            <select value={form.practice_area} onChange={e => set('practice_area', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
              {PA_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Icon + Color row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Icon</label>
              <input type="text" value={form.icon} onChange={e => set('icon', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Color</label>
              <div className="flex gap-2">
                <input type="color" value={form.color} onChange={e => set('color', e.target.value)}
                  className="w-10 h-9 rounded border border-gray-300 cursor-pointer" />
                <input type="text" value={form.color} onChange={e => set('color', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
            </div>
          </div>

          {/* Features */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Features</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.features.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                  {f}
                  <button onClick={() => set('features', form.features.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500">
                    <Minus className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-1">
              <input type="text" value={newFeature} onChange={e => setNewFeature(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                placeholder="Add feature..." className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              <button onClick={addFeature} className="px-2 py-1 text-xs bg-gray-100 border border-gray-200 rounded hover:bg-gray-200">
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Tags</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.tags.map((t, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-brand-50 text-brand-700">
                  {t}
                  <button onClick={() => set('tags', form.tags.filter((_, j) => j !== i))} className="text-brand-400 hover:text-red-500">
                    <Minus className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-1">
              <input type="text" value={newTag} onChange={e => setNewTag(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Add tag..." className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              <button onClick={addTag} className="px-2 py-1 text-xs bg-gray-100 border border-gray-200 rounded hover:bg-gray-200">
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Pricing */}
          <div className="border-t border-gray-100 pt-4">
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Pricing</label>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Type</label>
                <select value={form.price_type} onChange={e => {
                  const v = e.target.value;
                  set('price_type', v);
                  if (v === 'free') { set('price_cents', 0); set('price_description', 'Free'); set('price_interval', null); }
                  if (v === 'included') { set('price_cents', 0); set('price_description', 'Included in Starter and above'); set('price_interval', null); }
                }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                  <option value="free">Free</option>
                  <option value="included">Included in plan</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Description</label>
                <input type="text" value={form.price_description} onChange={e => set('price_description', e.target.value)}
                  placeholder="e.g. $49 one-time"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>
            </div>
            {form.price_type === 'premium' && (
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Price ($)</label>
                  <input type="number" min="0" step="1"
                    value={form.price_cents / 100}
                    onChange={e => set('price_cents', Math.round(parseFloat(e.target.value || 0) * 100))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Interval</label>
                  <select value={form.price_interval || ''} onChange={e => set('price_interval', e.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                    <option value="">None</option>
                    <option value="annual">Annual</option>
                    <option value="one_time">One-time</option>
                  </select>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_lite} onChange={e => set('is_lite', e.target.checked)}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                <label className="text-xs text-gray-600">Lite version</label>
              </div>
              {form.is_lite && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Full Version Slug</label>
                  <input type="text" value={form.full_version_slug} onChange={e => set('full_version_slug', e.target.value)}
                    placeholder="e.g. pre_litigation_mva"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <p className="text-xs text-gray-400">Saving will update to v{template.version + 1}</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || !form.name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
